import {FC, useEffect, useMemo, useRef, useState} from 'react'
import AsyncCreatableSelect from 'react-select/async-creatable'
import {useQuery} from '@tanstack/react-query'
import {v1_get_request_api} from '../api/get'
import {useDebounce} from '../_metronic/helpers'

type SelectProps = {
  label?: string
  name?: string
  placeholder: string
  options: {value: any; label: string}[]
  selected?: {value: any; label?: string}
  onSelect: (value: {value: any; label: string}) => void
  disabled?: boolean
  required?: boolean
  searchFn?: (val: string) => void
  createFn?: (val: string) => void
  error?: boolean
  className?: string
  onChange?: (val: any) => void
}

export const Select = (props: SelectProps) => {
  const [selected, setSelected] = useState<{value?: any; label?: string}>({
    value: '',
    label: '',
  })
  const [value, setValue] = useState('')
  const [localOptions, setLocalOptions] = useState<any>()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const options = useMemo(() => localOptions || props.options, [props.options, localOptions])

  useEffect(() => {
    if (!props.selected) return
    const {value, label} = props.selected
    setSelected(props.selected)
    setValue(label || value?.toString() || '')
  }, [props.selected?.value])

  const handleSelect = (item: {value: any; label: string}) => {
    setSelected(item)
    setValue(item.label)
    setIsOpen(false)
    props.onSelect(item)
  }

  const localSearchFn = (val: string) => {
    setLocalOptions(
      props.options.filter((option) => option.label.toLowerCase().includes(val.toLowerCase()))
    )
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <>
      <Styles />
      <div className='select-container' ref={dropdownRef}>
        {/* Input Box */}
        <div className='select-input' onClick={() => !props.disabled && setIsOpen(!isOpen)}>
          <input
            className='select-form-control'
            placeholder={props.placeholder}
            value={value}
            disabled={props.disabled}
            onChange={(e) => {
              const val = e.target.value
              props.searchFn ? props.searchFn(val) : localSearchFn(val)
              setValue(val)
            }}
            onClick={(e) => {
              e.stopPropagation()
              if (!props.disabled) setIsOpen((prev) => !prev)
            }}
          />
          <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
        </div>
        {/* Dropdown List */}
        {isOpen && (
          <div className='select-menu rounded border border-gray-300 shadow-lg'>
            <div
              className='select-item'
              onClick={() => {
                setSelected({value: '', label: ''})
                setIsOpen(false)
                props.onSelect({value: '', label: ''})
              }}
            >
              ------------
            </div>
            {options.length > 0 ? (
              options.map((item: any) => (
                <div
                  key={item.value}
                  className={`select-item ${
                    selected.value === item.value ? 'bg-primary/10 text-primary font-medium' : ''
                  }`}
                  onClick={() => handleSelect(item)}
                >
                  {item.label}
                </div>
              ))
            ) : (
              <>
                <div className='px-4 py-2 text-gray-500'>No record</div>
                {props.createFn && value && (
                  <div
                    className='px-4 py-2 cursor-pointer'
                    onClick={() => {
                      props.createFn?.(value)
                      setIsOpen(false)
                    }}
                  >
                    Create "{value}"
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}




export const EmailCreatableSelect: FC<{
  name: string
  placeholder?: string
  formik: any
  selectFn?: (val: any) => void
  createField?: string
}> = ({name, placeholder, formik, selectFn, createField}) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)


   const validateEmail = (email: string) => {
     return email.match(
       // eslint-disable-next-line no-useless-escape
       /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
     )
   }
  const {
    data: result,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['user', `search=${search}`],
    queryFn: v1_get_request_api,
    enabled: true,
  })


useEffect(() => {
  if (
    debouncedSearch !== undefined &&
    debouncedSearch.length >= 3 &&
    validateEmail(debouncedSearch)
  ) {
    refetch()
  }
}, [debouncedSearch])


  const loadOptions = (_: string, callback: (options: any[]) => void) => {
    const options =
      result?.entities?.slice(0, 5).map((u: any) => ({
        value: u.id,
        label: `${u.first_name} ${u.last_name} (${u.email})`,
        ...u,
      })) || []

    callback(options)
  }

  const value = formik.values[name] || formik.getFieldMeta(name).value
  const selected = useMemo(() => (value ? {label: value, value} : null), [value])

  const clearGroup = () => {
    const prefix = name.replace(/email$/, '')
    Object.keys(formik.values).forEach((key) => {
      if (key.startsWith(prefix)) {
        formik.setFieldValue(key, '')
      }
    })
  }

  return (
    <>
      <Styles />
      <AsyncCreatableSelect
        className='basic-single'
        classNamePrefix='select'
        cacheOptions
        loadOptions={loadOptions}
        defaultOptions
        placeholder={placeholder}
        isLoading={isFetching}
        value={selected}
        onInputChange={(val: any) => setSearch(val)}
        onChange={(e: any) => {
          const base = name.replace('email', '')
          if (e?.id) {
            formik.setFieldValue(base + 'id', e.id)
            e.first_name && formik.setFieldValue(base + 'first_name', e.first_name)
            e.last_name && formik.setFieldValue(base + 'last_name', e.last_name)
            e.email && formik.setFieldValue(name, e.email)
            e.phone && formik.setFieldValue(base + 'phone', e.phone)
            e.gender && formik.setFieldValue(base + 'gender', e.gender)
          } else {
            clearGroup()
            formik.setFieldValue(name, e?.value || '')
          }
          if (selectFn) selectFn(e?.id || e?.value)
        }}
        onCreateOption={(val: string) => {
          !validateEmail(val)
            ? formik.setFieldError(name, 'Invalid email address')
            : (() => {
                formik.setFieldError(name, '')

                const prefix = name.replace(/email$/, '')
                const userFields = formik.values?.account?.user || {}

                Object.keys(userFields).forEach((key) => {
                  const fullKey = `${prefix}${key}`
                  if (fullKey !== name) {
                    setTimeout(() => formik.setFieldValue(fullKey, ''), 0)
                  }
                })

                formik.setFieldValue(name, val)
                if (createField) formik.setFieldValue(createField, val)
                formik.validateForm()
              })()
          formik.validateForm()
        }}
      />
    </>
  )
  
}


export const Styles = () => (
  <style>{`
    .select-container {
      position: relative;
    }

    .select-input, .select__control {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 1rem;
      background-color: #f5f8fa;
      border: 1px solid transparent;
      border-radius: 0.475rem;
      height: auto;
      cursor: pointer;
      transition: background-color 0.2s ease;
      min-height: 46px;
      box-shadow: none;
    }

    .select-input:focus-within,
    .select__control--is-focused {
      background-color: #eef3f7;
      border-color: #ccc;
    }

    .select-input input,
    .select__input {
      border: none;
      outline: none;
      width: 100%;
      font-size: 16px;
      background: transparent;
      color: #3f4254;
      flex: 1;
      min-width: 50px;
    }

    .select__single-value {
      color: #3f4254;
    }

    .select-menu,
    .select__menu {
      position: absolute;
      z-index: 1055 !important;
      background-color: #fff;
      border-radius: 6px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      margin-top: 4px;
      max-height: 240px;
      overflow-y: auto;
      top: 100%;
      left: 0;
      right: 0;
    }

    .select-item,
    .select__option {
      padding: 10px 17px;
      font-size: 15px;
      border-bottom: 1px solid #eee;
      color: #3f4254;
      cursor: pointer;
    }

    .select-item:hover,
    .select__option--is-focused {
      background-color: #F1FAFF;
      color: #3699FF;
    }

    .select__option--is-selected {
      background-color: #F1FAFF;
      color: #3699FF;
      font-weight: 500;
    }
      .selected-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-right: 8px;
  }

  .selected-tag {
    background-color: #e9ecef;
    border-radius: 4px;
    padding: 2px 6px;
    display: flex;
    align-items: center;
    font-size: 14px;
  }

  .selected-tag-remove {
    margin-left: 4px;
    cursor: pointer;
    color: #6c757d;
  }

    .selected-tag-remove:hover {
    color: #dc3545;
  }

  /* === File Selector Styles === */
.file-wrapper {
  margin: 0.1rem 0;
}

.file-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.3rem;
  border: 1px solid #f5f8fa;
  border-radius: 0.375rem;
  background-color: #f5f8fa;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.file-container:hover {
  background-color: #f9fafb;
}

.file-container.disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.file-container.error {
  border-color: #f87171;
}

.custom-file-input {
  display: none;
}

.file-name {
  color: #64748b;
  font-size: 0.95rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 0.75rem;
}

.custom-button {
  display: flex;
  background-color: #3699FF;
  color: white;
  font-weight: 500;
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  transition: background-color 0.2s ease;
  white-space: nowrap;
}

.custom-button:hover {
  background-color: #2779bd;
}

.file-error {
  color: #f87171;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}

/* === Image Selector Styles === */
.image-wrapper {
  position: relative;
  width: 125px;
  height: 125px;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  background-color: #f9fafb;
  background-size: cover;
  background-position: center;
  overflow: hidden;
  cursor: pointer;
}

.image-controls {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  gap: 4px;
}

.image-btn {
  width: 25px;
  height: 25px;
  background-color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.2);
}

.image-btn:hover {
  background-color: #edf2f7;
}

.image-input-hidden {
  display: none;
}

.image-error {
  color: #f87171;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}
  `}</style>
)
